import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "../auth";
import { api, ApiError, type Farm } from "../api";

/** Farm Control shell — owner's full-screen working surface.
 *
 *  Renders a thin top bar (farm switcher on the left, user menu on the
 *  right) above an iframe that loads the real master_frontend through
 *  the reverse tunnel at /app/?farm=<id>. The iframe gets the rest of
 *  the viewport so the in-frame app feels native.
 *
 *  Farm switch = react-router navigate to /farms/<new>/ which re-renders
 *  this shell with a new iframe src. Full bundle reload is fine — the
 *  tunnel handshake is <1s and baseApi.ts reads __APP_CONFIG__ once at
 *  module init anyway, so in-place reconfig wouldn't save meaningful time.
 */
export function FarmShell() {
  const { farmId = "" } = useParams<{ farmId: string }>();
  const [pwModal, setPwModal] = useState(false);

  useEffect(() => {
    if (farmId) localStorage.setItem("cloud_last_farm_id", farmId);
  }, [farmId]);

  return (
    // 100dvh (dynamic viewport) instead of 100vh so iOS Safari's bottom
    // URL bar doesn't eat the last ~80px — that's where master_frontend
    // paints its tab bar, and with 100vh it was being covered.
    <div style={{ display: "flex", flexDirection: "column", height: "100dvh" }}>
      <div
        style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "6px 12px", borderBottom: "1px solid var(--border, #e2e8f0)",
          background: "var(--surface, #fff)", minHeight: 44, flexShrink: 0,
        }}
      >
        <Link to="/" style={{ textDecoration: "none", fontSize: "0.9rem", fontWeight: 600 }}>
          🌿 <span className="farm-top-bar-brand-text">Irrigation Cloud</span>
        </Link>
        <div style={{ flex: 1 }}>
          <FarmSwitcher currentFarmId={farmId} />
        </div>
        <UserMenu onChangePassword={() => setPwModal(true)} />
      </div>

      <iframe
        key={farmId}
        src={`/app/?farm=${farmId}`}
        title="Farm Control"
        style={{ flex: 1, width: "100%", border: "none" }}
      />

      {pwModal && <ChangePasswordModal onClose={() => setPwModal(false)} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Farm switcher — dropdown listing owner's farms; selecting navigates
// to /farms/<id>/ which re-renders FarmShell with fresh iframe src.
// ═══════════════════════════════════════════════════════════════════

function FarmSwitcher({ currentFarmId }: { currentFarmId: string }) {
  const navigate = useNavigate();
  const [farms, setFarms] = useState<Farm[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      try {
        setFarms(await api.get<Farm[]>("/farms"));
      } catch {
        // Network error or tenant has no owned farms — silent; the button
        // just shows "Đang tải…" / current name without a chooser.
      }
    })();
  }, []);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [open]);

  const current = farms.find((f) => f.id === currentFarmId);
  const label = current?.name || "Đang tải…";

  function pick(f: Farm) {
    setOpen(false);
    if (f.id !== currentFarmId) navigate(`/farms/${f.id}/`);
  }

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "4px 10px", fontSize: "0.85rem", fontWeight: 500,
          border: "1px solid var(--border, #e2e8f0)", borderRadius: 6,
          background: "#f8fafc", cursor: "pointer",
        }}
      >
        <span>🚜</span>
        <span style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label}
        </span>
        <span style={{ fontSize: "0.7rem", color: "var(--muted, #94a3b8)" }}>{open ? "▴" : "▾"}</span>
      </button>
      {open && farms.length > 0 && (
        <div
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 20,
            background: "var(--surface, #fff)", border: "1px solid var(--border, #e2e8f0)",
            borderRadius: 6, minWidth: 260, maxHeight: 320, overflow: "auto",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          {farms.map((f) => (
            <button
              key={f.id}
              onClick={() => pick(f)}
              style={{
                width: "100%", textAlign: "left", padding: "10px 12px",
                border: "none", borderBottom: "1px solid var(--border, #e2e8f0)",
                background: f.id === currentFarmId ? "var(--ok-bg, #dcfce7)" : "transparent",
                fontWeight: f.id === currentFarmId ? 600 : 400,
                fontSize: "0.85rem", cursor: "pointer",
              }}
            >
              {f.name}
              <div style={{ fontSize: "0.7rem", color: "var(--muted, #94a3b8)" }}>
                <code>{f.slug}</code> · {f.timezone}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// User menu — identity + links to cloud-side pages (settings, admin,
// change password, logout). Dropdown anchored top-right.
// ═══════════════════════════════════════════════════════════════════

function UserMenu({ onChangePassword }: { onChangePassword: () => void }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [open]);

  if (!user) return null;
  const initial = (user.email || "?").charAt(0).toUpperCase();

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="User menu"
        style={{
          width: 32, height: 32, borderRadius: "50%",
          background: user.is_platform_admin ? "#7c3aed" : "#0ea5e9",
          color: "white", border: "none", fontSize: "0.85rem", fontWeight: 600,
          cursor: "pointer",
        }}
      >
        {initial}
      </button>
      {open && (
        <div
          style={{
            position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 20,
            background: "var(--surface, #fff)", border: "1px solid var(--border, #e2e8f0)",
            borderRadius: 6, minWidth: 220, overflow: "hidden",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--border, #e2e8f0)" }}>
            <div style={{ fontSize: "0.85rem", fontWeight: 500 }}>{user.email}</div>
            <div style={{ fontSize: "0.7rem", color: "var(--muted, #94a3b8)" }}>
              {user.is_platform_admin ? "platform admin" : user.role}
            </div>
          </div>
          <MenuLink to="/settings/profile" icon="👤">Tài khoản</MenuLink>
          <MenuLink to="/settings/farms" icon="🚜">Quản lý Farms</MenuLink>
          <MenuLink to="/settings/users" icon="👥">Quản lý Users</MenuLink>
          {user.is_platform_admin && (
            <MenuLink to="/admin/owners" icon="🛡">Platform Admin</MenuLink>
          )}
          <MenuButton onClick={onChangePassword} icon="🔑">Đổi mật khẩu</MenuButton>
          <MenuButton onClick={logout} icon="🚪" danger>Đăng xuất</MenuButton>
        </div>
      )}
    </div>
  );
}

function MenuLink({ to, icon, children }: { to: string; icon: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "8px 12px", fontSize: "0.85rem", color: "inherit",
        textDecoration: "none", borderBottom: "1px solid var(--border, #e2e8f0)",
      }}
    >
      <span>{icon}</span>{children}
    </Link>
  );
}

function MenuButton({
  onClick, icon, danger = false, children,
}: { onClick: () => void; icon: string; danger?: boolean; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        width: "100%", padding: "8px 12px", fontSize: "0.85rem",
        background: "transparent", border: "none",
        borderBottom: "1px solid var(--border, #e2e8f0)", textAlign: "left",
        cursor: "pointer", color: danger ? "#dc2626" : "inherit",
      }}
    >
      <span>{icon}</span>{children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Change-password modal (used by the user-menu dropdown).
// ═══════════════════════════════════════════════════════════════════

function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPw !== confirmPw) {
      setError("Mật khẩu mới và xác nhận không khớp");
      return;
    }
    if (newPw.length < 8) {
      setError("Mật khẩu mới phải từ 8 ký tự");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/me/change-password", {
        current_password: currentPw, new_password: newPw,
      });
      setSuccess(true);
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Đổi mật khẩu thất bại");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <h2>Đổi mật khẩu</h2>
        {success ? (
          <>
            <div className="alert ok">Mật khẩu đã được cập nhật.</div>
            <div className="btn-row">
              <button type="button" className="primary" onClick={onClose}>Đóng</button>
            </div>
          </>
        ) : (
          <form onSubmit={onSubmit}>
            {error && <div className="alert error">{error}</div>}
            <div className="field">
              <label>Mật khẩu hiện tại</label>
              <input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required autoFocus autoComplete="current-password" />
            </div>
            <div className="field">
              <label>Mật khẩu mới (min 8)</label>
              <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={8} autoComplete="new-password" />
            </div>
            <div className="field">
              <label>Xác nhận</label>
              <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required minLength={8} autoComplete="new-password" />
            </div>
            <div className="btn-row">
              <button type="button" onClick={onClose}>Huỷ</button>
              <button type="submit" className="primary" disabled={submitting}>
                {submitting ? "Đang đổi…" : "Đổi mật khẩu"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
